/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable max-lines */

import { upsertCohort } from 'actions/cohorts';
import LoadingIndicator from 'components/LoadingIndicator';
import Modal from 'components/Modal';
import { CLOSE_ICON } from 'constants/envelopeIcons';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { getFileUploadsUploading } from 'selectors/fileUploads';
import { useSelector } from 'store';
import { Cohort } from 'types';

export const lenghtOfWorkOptions = [
  {
    value: '',
    label: 'Length of work',
  },
  {
    value: '3',
    label: '3 months',
  },
  {
    value: '6',
    label: '6 months',
  },
  {
    value: '12',
    label: '12 months',
  },
];

type ComponentProps = {
  cohort: Cohort | null;
  isModalOpen: boolean;
  toggleModalOpen: () => void;
  isEditing?: boolean;
};

const CreateCohortModal: React.FC<ComponentProps> = ({
  cohort,
  isModalOpen,
  toggleModalOpen,
  isEditing = false,
}) => {
  const dispatch = useDispatch();

  const uploadLoading = useSelector(getFileUploadsUploading);

  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>(cohort?.config.contract_filename || '');
  const [uploading, setUploading] = useState(false);

  const { register, getValues, handleSubmit } = useForm<{
    name: string;
    insight: string;
    prevent: string;
    notes: string;
  }>({
    defaultValues: {
      name: cohort?.name || '',
      insight: cohort?.config.insight_seats.toString() || '',
      prevent: cohort?.config.prevent_seats.toString() || '',
      notes: cohort?.config.notes || '',
    },
  });

  useEffect(() => {
    if (uploadLoading) {
      setUploading(true);
    }
  }, [uploadLoading]);

  useEffect(() => {
    if (!uploadLoading && uploading) {
      setUploading(false);
      toggleModalOpen();
    }
  }, [uploadLoading, uploading, toggleModalOpen]);

  const onSubmit = (redirectToImport = false): void => {
    const { name, insight, prevent, notes } = getValues();

    if (file) {
      // add file
      dispatch(
        upsertCohort({
          cohort: {
            ...cohort,
            name,
            config: {
              ...cohort?.config,
              insight_seats: Number.parseInt(insight, 10),
              prevent_seats: Number.parseInt(prevent, 10),
              contract_filename: file.name,
              notes,
            },
            file,
            // @ts-ignore
            contract_filename: file.name,
          },
          action: 'add',
          redirectToImport,
        })
      );
    } else {
      if (
        (fileName && cohort?.config.contract_filename) ||
        (!fileName && !cohort?.config.contract_filename)
      ) {
        // don't touch file
        dispatch(
          upsertCohort({
            cohort: {
              ...(cohort?.uuid ? { uuid: cohort?.uuid } : {}),
              name,
              config: {
                ...cohort?.config,
                insight_seats: Number.parseInt(insight, 10),
                prevent_seats: Number.parseInt(prevent, 10),
                notes,
              },
            },
            redirectToImport,
          })
        );
        toggleModalOpen();
        return;
      }

      if (!fileName && cohort?.config.contract_filename) {
        // delete file
        dispatch(
          upsertCohort({
            cohort: {
              ...(cohort?.uuid ? { uuid: cohort?.uuid } : {}),
              name,
              config: {
                ...cohort?.config,
                insight_seats: Number.parseInt(insight, 10),
                prevent_seats: Number.parseInt(prevent, 10),
                notes,
              },
            },
            action: 'delete',
            redirectToImport,
          })
        );
      }
    }
    toggleModalOpen();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files != null) {
      setFile(event.target.files[0]);
      setFileName(event.target.files[0].name);
    }
  };

  const handleSaveButton = (): void => {
    handleSubmit(() => onSubmit())();
  };

  const handleRemoveFile = (): void => {
    setFile(null);
    setFileName('');
  };

  const handleImportData = (): void => {
    handleSubmit(() => onSubmit(true))();
  };

  const renderCreateReviewSet = (
    <div className="w-full flex flex-row pt-4 pb-10 text-body">
      <form className="w-full h-full flex flex-row gap-2">
        <div className="pr-20 w-1/2 flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label htmlFor="priority" className="flex flex-col gap-2">
              <div className="flex flex-row gap-1">
                <span>Name</span>
                <span className="litlingo-red-color">*</span>
              </div>

              <input
                id="name"
                type="text"
                ref={register({ required: true, pattern: /\S/i })}
                name="name"
                placeholder=""
                className="form-input h-9 w-88 p-2 text-body rounded placeholder-italic placeholder-litlingo-gray-6"
              />
            </label>

            <label htmlFor="priority" className="flex flex-row items-center gap-4">
              <div className="flex flex-row gap-1">
                <span>Number of Insight Seats</span>
                <span className="litlingo-red-color">*</span>
              </div>

              <input
                id="insight"
                type="number"
                ref={register({ required: true, min: 0 })}
                name="insight"
                placeholder="0"
                className="form-input h-9 w-20 p-2 text-body rounded placeholder-italic placeholder-litlingo-gray-6"
              />
            </label>

            <label htmlFor="priority" className="flex flex-row items-center gap-2.5">
              <div className="flex flex-row gap-1">
                <span>Number of Prevent Seats</span>
                <span className="litlingo-red-color">*</span>
              </div>

              <input
                id="prevent"
                type="number"
                ref={register({ required: true, min: 0 })}
                name="prevent"
                placeholder="0"
                className="form-input h-9 w-20 p-2 text-body rounded placeholder-italic placeholder-litlingo-gray-6"
              />
            </label>
          </div>

          <div className="flex flex-row gap-4 items-center">
            {fileName && (
              <div className="flex flex-row">
                <button type="button" className="focus:outline-none" onClick={handleRemoveFile}>
                  {CLOSE_ICON}
                </button>

                <span className="ml-2 max-w-104 truncate underline">{fileName.split('.')[0]}</span>
                <span className="underline">.{fileName.split('.').pop()}</span>
              </div>
            )}

            <label
              htmlFor="file-input"
              className="self-end w-36 h-8 inline-flex justify-center items-center px-4 py-2 text-body rounded bg-white border border-litlingo-gray-3 cursor-pointer"
            >
              {uploadLoading ? (
                <LoadingIndicator size="5" />
              ) : (
                <span className="text-litlingo-gray-5 font-bold">Add SOW</span>
              )}

              <input id="file-input" type="file" className="hidden" onChange={handleInputChange} />
            </label>
          </div>
        </div>

        <div className="flex items-stretch border-r border-litlingo-gray-3" />

        <div className="w-1/2 flex">
          <div className="w-88 mx-auto flex flex-col gap-1">
            <span>Notes</span>

            <textarea
              id="notes"
              ref={register()}
              name="notes"
              rows={7}
              className="form-input w-full p-2 text-body rounded-md resize-none placeholder-italic placeholder-litlingo-gray-6 border-litlingo-gray-2"
            />
          </div>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {isModalOpen && (
        <Modal
          body={renderCreateReviewSet}
          title={isEditing ? cohort?.name : 'New Cohort'}
          okButton
          okButtonText={isEditing ? 'Save' : 'Import Data'}
          okButtonOnClick={isEditing ? handleSaveButton : handleImportData}
          toggleShowModal={toggleModalOpen}
          cancelButton
          cancelButtonText={isEditing ? 'Cancel' : 'Save'}
          cancelButtonOnclick={isEditing ? toggleModalOpen : handleSaveButton}
          style={{ width: '65rem' }}
        />
      )}
    </>
  );
};

export default CreateCohortModal;
